import { addroomText } from 'owa-locstrings/lib/strings/addroomtext.locstring.json';
import { browseWithRoomFinderTitle } from 'owa-locstrings/lib/strings/browsewithroomfinder.locstring.json';
import { findLocationInfo_NoMatchFound } from 'owa-locstrings/lib/strings/findlocationinfo_nomatchfound.locstring.json';
import { findLocationInfo_Loading } from 'owa-locstrings/lib/strings/findlocationinfo_loading.locstring.json';
import { findLocationInfo_Searching } from 'owa-locstrings/lib/strings/findlocationinfo_searching.locstring.json';
import { useThisLocationText } from 'owa-locstrings/lib/strings/usethislocationtext.locstring.json';
import { suggestedLocationsText } from 'owa-locstrings/lib/strings/suggestedlocationstext.locstring.json';
import loc, { format } from 'owa-localize';
import RequestStatus from '../store/schema/RequestStatus';
import type { ITheme } from '@fluentui/style-utilities';
import type { ISuggestionsHeaderFooterProps } from '@fluentui/react/lib/FloatingPicker';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { isNullOrWhiteSpace } from 'owa-string-utils';
import { isFeatureEnabled } from 'owa-feature-flags';
import { Icon } from '@fluentui/react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';
import * as React from 'react';
import generateLinkStyle from 'owa-component-styling-utils/lib/utils/colorSchemesToStyles/generateLinkStyle';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { Link } from '@fluentui/react/lib/Link';
import { AriaRoles } from 'owa-accessibility';

const styles = require('../components/LocationCommonWell.scss');
let classNames = require('classnames');

export function getHeaderItemProps(
    queryString: string,
    numOfSuggestions: number,
    colorScheme: ColorScheme,
    disableUseThisLocationButton: boolean,
    onUseThisLocationClicked: (locationString: string) => void
): ISuggestionsHeaderFooterProps[] {
    const hasQueryString = !isNullOrWhiteSpace(queryString);
    const headerTextColorStyle = generateLinkStyle(colorScheme);
    const useThisLocationHeaderText = format(loc(useThisLocationText), queryString);

    return [
        {
            renderItem: () => {
                return (
                    <span className={styles.header}>
                        <Link styles={headerTextColorStyle} disabled={true}>
                            {loc(suggestedLocationsText)}
                        </Link>
                    </span>
                );
            },
            shouldShow: () => {
                return !hasQueryString && numOfSuggestions > 0;
            },
        },
        {
            ariaLabel: useThisLocationHeaderText,
            renderItem: () => {
                return (
                    <span className={classNames(styles.header, styles.useThisLocationHeader)}>
                        <Link styles={headerTextColorStyle}>{useThisLocationHeaderText}</Link>
                    </span>
                );
            },
            shouldShow: () => {
                // Show 'Use this location' when there is text in the input field
                return hasQueryString && !disableUseThisLocationButton;
            },
            onExecute: () => {
                onUseThisLocationClicked(queryString);
            },
        },
    ];
}

export function getFooterItemProps(
    queryString: string,
    findLocationRequestStatus: RequestStatus,
    numOfSuggestions: number,
    theme: ITheme,
    showAddRoom: boolean,
    onAddRoomClicked: () => void
): ISuggestionsHeaderFooterProps[] {
    const hasQueryString = !isNullOrWhiteSpace(queryString);
    const addRoomFooterText = isFeatureEnabled('cal-advancedRoomBooking')
        ? loc(browseWithRoomFinderTitle)
        : loc(addroomText);
    return [
        {
            renderItem: () => {
                const footerText = hasQueryString
                    ? format(loc(findLocationInfo_Searching), queryString)
                    : loc(findLocationInfo_Loading);
                return (
                    <div className={styles.footer}>
                        <Spinner className={styles.spinner} theme={theme} />
                        <span className={styles.searchingText}>{footerText}</span>
                    </div>
                );
            },
            shouldShow: () => {
                // Show the spinner when the findLocation service request is pending and we have no results to show
                return numOfSuggestions == 0 && findLocationRequestStatus === RequestStatus.Pending;
            },
        },
        {
            renderItem: () => {
                return (
                    <div className={classNames(styles.footer, styles.noResult)}>
                        {loc(findLocationInfo_NoMatchFound)}
                    </div>
                );
            },
            shouldShow: () => {
                return (
                    hasQueryString &&
                    numOfSuggestions == 0 &&
                    findLocationRequestStatus !== RequestStatus.Pending
                );
            },
        },
        {
            ariaLabel: addRoomFooterText,
            renderItem: () => {
                return (
                    <div
                        tabIndex={0}
                        role={AriaRoles.button}
                        className={classNames(styles.footer, styles.addRoom)}
                        key="roomItem">
                        <div className={styles.addRoomIcon}>
                            <Icon iconName={ControlIcons.Add} />
                        </div>
                        <span className={styles.addRoomText}>{addRoomFooterText}</span>
                    </div>
                );
            },

            shouldShow: () => {
                return showAddRoom;
            },

            onExecute: () => {
                onAddRoomClicked();
            },
        },
    ];
}
